import {BUILD_SHA, BUILD_VERSION} from "./buildInfo";
import {serializeEnvelope} from "./requestEnvelope";

type ExportContext = {
  kind: string;
  product?: string;
  channel?: string;
  type?: string;
};

type ExportAnalyticsContext = {
  role: string;
  kind: string;
  renderer?: string;
  selection?: string;
  durationMs?: number;
};

type ExportCompletionContext = ExportAnalyticsContext & {
  renderer: string;
};

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function errorCause(error: unknown): string | null {
  if (!(error instanceof Error) || !(error.cause instanceof Error)) return null;
  return error.cause.message;
}

function errorEnvelope(error: unknown): Record<string, unknown> | null {
  if (!error || typeof error !== "object" || !("envelope" in error)) return null;
  const envelope = error.envelope;
  return envelope && typeof envelope === "object" ? (envelope as Record<string, unknown>) : null;
}

function resourceErrorDetails(error: unknown): Record<string, unknown> {
  if (!error || typeof error !== "object") return {};
  const record = error as Record<string, unknown>;
  const details: Record<string, unknown> = {};
  for (const key of ["resourceUrl", "resourceType", "attempts", "status"] as const) {
    if (record[key] !== undefined) details[key] = record[key];
  }
  return details;
}

export function buildExportFailure(error: unknown, context: ExportContext) {
  const message = errorMessage(error);
  const errorDetails = {
    name: error instanceof Error ? error.name : "Error",
    message,
    cause: errorCause(error),
    ...resourceErrorDetails(error),
  };
  const envelope = errorEnvelope(error);
  const diagnostics = envelope
    ? serializeEnvelope(envelope, {
        action: "export",
        ...context,
        error: {
          ...(envelope.error && typeof envelope.error === "object" ? envelope.error : {}),
          ...errorDetails,
        },
      })
    : {
        action: "export",
        ...context,
        error: errorDetails,
      };

  return {
    title: `Couldn't export ${context.kind}`,
    summary: `The export failed: ${message}`,
    details: ["No partial export was downloaded. Fix the reported problem and try again."],
    diagnostics,
  };
}

export function buildExportCompletionAnalytics({
  role,
  kind,
  renderer,
  selection,
  durationMs,
}: ExportCompletionContext): {
  role: string;
  kind: string;
  renderer: string;
  selection?: string;
  durationMs?: number;
  buildSha: string;
  buildVersion: string;
} {
  return {
    role,
    kind: kind.toLowerCase(),
    renderer,
    ...(selection ? {selection} : {}),
    ...(durationMs !== undefined ? {durationMs} : {}),
    buildSha: BUILD_SHA,
    buildVersion: BUILD_VERSION,
  };
}

export function buildExportFailureAnalytics(
  error: unknown,
  context: ExportAnalyticsContext
): {role: string; kind: string; error: string; errorName: string} & Record<string, unknown> {
  return {
    role: context.role,
    kind: context.kind.toLowerCase(),
    error: errorMessage(error),
    errorName: error instanceof Error ? error.name : "Error",
    ...resourceErrorDetails(error),
    ...(context.renderer ? {renderer: context.renderer} : {}),
    ...(context.selection ? {selection: context.selection} : {}),
    ...(context.durationMs !== undefined ? {durationMs: context.durationMs} : {}),
    buildSha: BUILD_SHA,
    buildVersion: BUILD_VERSION,
  };
}
